import {derived, writable} from 'svelte/store';
import {resettable} from '../utils/store';
import {replaceStateWithQuery} from '../utils/query-state';

import type {
  Confidentiality,
  DiamondSector,
  ProjectType,
  Visibility
} from '../types/project';
import type {DateRange} from '../types/date';

// The search text to filter the projects
export const searchText = resettable<string>('');

// The type to filter the projects
export const typeFilter = resettable<ProjectType | null>(null);

// The confidentiality to filter the projects
export const confidentialityFilter = resettable<Confidentiality | null>(null);

// The visibility to filter the projects
export const visibilityFilter = resettable<Visibility | null>(null);

// The diamond elements to filter the projects
export const diamondFilter = resettable<Array<DiamondSector>>([]);

// A list of customers to filter the projects
export const customersFilter = resettable<Array<string>>([]);

// A list of team members and project leads to filter the projects
export const teamFilter = resettable<Array<string>>([]);

// A list of fields to filter the projects
export const fieldsFilter = resettable<Array<string>>([]);

// A list of topics to filter the projects
export const topicsFilter = resettable<Array<string>>([]);

// A list of services to filter the projects
export const servicesFilter = resettable<Array<string>>([]);

// Whether to show project drafts or not
export const showDrafts = resettable<boolean>(true);

// A list of tasks to filter the projects
export const tasksFilter = resettable<Array<string>>([]);

// A list of technologies to filter the projects
export const technologiesFilter = resettable<Array<string>>([]);

// A list of formats to filter the projects
export const formatsFilter = resettable<Array<string>>([]);

// A list of methods to filter the projects
export const methodsFilter = resettable<Array<string>>([]);

// A value in time to filter the projects
export const minValueInTimeFilter = resettable<number | null>(null);
export const maxValueInTimeFilter = resettable<number | null>(null);

// A value in price to filter the projects
export const minValueInPriceFilter = resettable<number | null>(null);
export const maxValueInPriceFilter = resettable<number | null>(null);

// A start date to filter the projects
export const startDateFilter = resettable<Date | null>(null);

// A end date to filter the projects
export const endDateFilter = resettable<Date | null>(null);

// A date range to filter the projects
export const dateRangeFilter = derived(
  [startDateFilter, endDateFilter],
  ([$startDateFilter, $endDateFilter]) =>
    ({
      start: $startDateFilter,
      end: $endDateFilter
    } as DateRange)
);

export const filterQueryParameters = derived(
  [
    searchText,
    typeFilter,
    confidentialityFilter,
    visibilityFilter,
    diamondFilter,
    customersFilter,
    teamFilter,
    fieldsFilter,
    topicsFilter,
    servicesFilter,
    startDateFilter,
    endDateFilter,
    tasksFilter,
    technologiesFilter,
    formatsFilter,
    methodsFilter,
    minValueInTimeFilter,
    maxValueInTimeFilter,
    minValueInPriceFilter,
    maxValueInPriceFilter
  ],
  ([
    $searchText,
    $typeFilter,
    $confidentialityFilter,
    $visibilityFilter,
    $diamondFilter,
    $customersFilter,
    $teamFilter,
    $fieldsFilter,
    $topicsFilter,
    $servicesFilter,
    $startDateFilter,
    $endDateFilter,
    $tasksFilter,
    $technologiesFilter,
    $formatsFilter,
    $methodsFilter,
    $minValueInTimeFilter,
    $maxValueInTimeFilter,
    $minValueInPriceFilter,
    $maxValueInPriceFilter
  ]) => ({
    search: $searchText,
    type: $typeFilter,
    confidentiality: $confidentialityFilter,
    visibility: $visibilityFilter,
    diamond: $diamondFilter,
    customers: $customersFilter,
    team: $teamFilter,
    fields: $fieldsFilter,
    topics: $topicsFilter,
    services: $servicesFilter,
    startDate: $startDateFilter?.toISOString(),
    endDate: $endDateFilter?.toISOString(),
    tasks: $tasksFilter,
    technologies: $technologiesFilter,
    formats: $formatsFilter,
    methods: $methodsFilter,
    minValueInTime: $minValueInTimeFilter,
    maxValueInTime: $maxValueInTimeFilter,
    minValueInPrice: $minValueInPriceFilter,
    maxValueInPrice: $maxValueInPriceFilter
  })
);

filterQueryParameters.subscribe(replaceStateWithQuery);

// Add toggle method to sort direction store
function createSortDirectionStore() {
  const {subscribe, update} = writable<'asc' | 'desc'>('desc');

  return {
    subscribe,
    toggle: () => update(value => (value === 'desc' ? 'asc' : 'desc'))
  };
}

// The direction to sort the projects ('asc' or 'desc')
export const sortDirection = createSortDirectionStore();
